import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { AttributeChips } from './AttributeChips';
import { SynonymsInput } from './SynonymsInput';
import { useAgentConceptCard } from './useAgentConceptCard';
import { type AgentConcept } from '../../state/createProjectSlice';

// TODO: cleanup with cp_annotation_createproject_agent ff

interface AgentConceptCardProps {
  conceptGroupId: string;
  concept: AgentConcept;
  alias: string | null;
  attributeLabels: string[];
}

interface ReadOnlyFieldProps {
  label: string;
  children: ReactNode;
}

const ReadOnlyField = ({ label, children }: ReadOnlyFieldProps) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography
        component="dt"
        variant="caption"
        sx={{ color: theme.palette.text.secondary, display: 'block' }}
      >
        {label}
      </Typography>
      <Box component="dd" sx={{ m: 0, mt: theme.spacing(1) }}>
        {children}
      </Box>
    </Box>
  );
};

/**
 * Step 5 concept card. The layout follows the step 2 card shell; it does not use the
 * step 2 card, whose hook dispatches step 2 actions.
 */
export const AgentConceptCard = ({
  conceptGroupId,
  concept,
  alias,
  attributeLabels,
}: AgentConceptCardProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const {
    isExpanded,
    draft,
    isNameBlank,
    handleToggleExpanded,
    handleEdit,
    handleDraftChange,
    handleSave,
    handleCancel,
  } = useAgentConceptCard({ conceptGroupId, concept });

  const idPrefix = `agent-concept-${conceptGroupId}`;
  const bodyId = `${idPrefix}-body`;
  const none = formatMessage({ id: 'createProjectWizard.step.annotationAgent.concept.none' });

  return (
    <Box
      id={idPrefix}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${borderRadius.md}px`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(2),
          p: theme.spacing(4),
        }}
      >
        <Typography
          id={`${idPrefix}-title`}
          variant="body1"
          sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {alias === null ? (
            <Box component="span" sx={{ fontWeight: theme.typography.fontWeightMedium }}>
              {concept.name}
            </Box>
          ) : (
            formatMessage(
              { id: 'createProjectWizard.step.annotationAgent.concept.header' },
              {
                name: (
                  <Box component="span" sx={{ fontWeight: theme.typography.fontWeightMedium }}>
                    {concept.name}
                  </Box>
                ),
                alias: (
                  <Box component="span" sx={{ color: theme.palette.secondary.dark }}>
                    {alias}
                  </Box>
                ),
              }
            )
          )}
        </Typography>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          <IconButton
            id={`${idPrefix}-expand-toggle`}
            size="small"
            onClick={handleToggleExpanded}
            aria-expanded={isExpanded}
            aria-controls={bodyId}
            aria-label={formatMessage(
              {
                id: isExpanded
                  ? 'createProjectWizard.step.annotationAgent.concept.collapse'
                  : 'createProjectWizard.step.annotationAgent.concept.expand',
              },
              { name: concept.name }
            )}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          {draft === null && (
            <IconButton
              id={`${idPrefix}-edit-button`}
              size="small"
              onClick={handleEdit}
              aria-label={formatMessage(
                { id: 'createProjectWizard.step.annotationAgent.concept.edit' },
                { name: concept.name }
              )}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
      {isExpanded && (
        <Box
          id={bodyId}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            px: theme.spacing(4),
            pt: theme.spacing(4),
            pb: theme.spacing(5),
          }}
        >
          {draft ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
              <>
                <TextField
                  id={`${idPrefix}-name`}
                  fullWidth
                  size="small"
                  label={formatMessage({
                    id: 'createProjectWizard.step.annotationAgent.concept.field.name',
                  })}
                  value={draft.name}
                  onChange={(event) => handleDraftChange({ name: event.target.value })}
                  error={isNameBlank}
                  helperText={
                    isNameBlank
                      ? formatMessage({
                          id: 'createProjectWizard.step.annotationAgent.concept.name.required',
                        })
                      : undefined
                  }
                />
                {alias !== null && (
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {formatMessage(
                      { id: 'createProjectWizard.step.annotationAgent.concept.alias.caption' },
                      { alias }
                    )}
                  </Typography>
                )}
              </>
              <SynonymsInput
                id={`${idPrefix}-synonyms`}
                synonyms={draft.synonyms}
                onChange={(synonyms) => handleDraftChange({ synonyms })}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: theme.spacing(1) }}>
                  {formatMessage({
                    id: 'createProjectWizard.step.annotationAgent.concept.field.attributes.draft',
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: 'block',
                    mb: theme.spacing(2),
                  }}
                >
                  {formatMessage({
                    id: 'createProjectWizard.step.annotationAgent.concept.attributes.readOnly',
                  })}
                </Typography>
                <AttributeChips
                  labels={attributeLabels}
                  emptyLabel={formatMessage({
                    id: 'createProjectWizard.step.annotationAgent.concept.attributes.empty',
                  })}
                />
              </Box>
              <TextField
                id={`${idPrefix}-notes`}
                fullWidth
                multiline
                minRows={3}
                size="small"
                label={formatMessage({
                  id: 'createProjectWizard.step.annotationAgent.concept.field.notes.draft',
                })}
                placeholder={formatMessage({
                  id: 'createProjectWizard.step.annotationAgent.concept.notes.placeholder',
                })}
                value={draft.notes}
                onChange={(event) => handleDraftChange({ notes: event.target.value })}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(2) }}>
                <Button
                  id={`${idPrefix}-cancel-button`}
                  variant="outlined"
                  displayLabel={formatMessage({
                    id: 'createProjectWizard.step.annotationAgent.concept.cancel',
                  })}
                  onClickHandler={handleCancel}
                  sx={{ textTransform: 'none' }}
                />
                <Button
                  id={`${idPrefix}-save-button`}
                  variant="contained"
                  displayLabel={formatMessage({
                    id: 'createProjectWizard.step.annotationAgent.concept.save',
                  })}
                  onClickHandler={handleSave}
                  disabled={isNameBlank}
                  sx={{ textTransform: 'none', backgroundColor: theme.palette.primary.main }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              component="dl"
              sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}
            >
              <ReadOnlyField
                label={formatMessage({
                  id: 'createProjectWizard.step.annotationAgent.concept.field.name',
                })}
              >
                <Typography variant="body2">{concept.name}</Typography>
              </ReadOnlyField>
              <ReadOnlyField
                label={formatMessage({
                  id: 'createProjectWizard.step.annotationAgent.concept.field.synonyms',
                })}
              >
                <Typography variant="body2">
                  {concept.synonyms.length > 0 ? concept.synonyms.join(', ') : none}
                </Typography>
              </ReadOnlyField>
              <ReadOnlyField
                label={formatMessage({
                  id: 'createProjectWizard.step.annotationAgent.concept.field.attributes',
                })}
              >
                <AttributeChips labels={attributeLabels} />
              </ReadOnlyField>
              <ReadOnlyField
                label={formatMessage({
                  id: 'createProjectWizard.step.annotationAgent.concept.field.notes',
                })}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {concept.notes.trim() ? concept.notes : none}
                </Typography>
              </ReadOnlyField>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
